import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { MdShare, MdModeEdit, MdClose } from "react-icons/md";
import { HiOutlineChevronLeft } from "react-icons/hi2";
import toast from "react-hot-toast";

import { AppContext } from "./context";
import { copyToClipboard } from "../../../../../utils";

export default function Header() {
  const { index, setIndex, workshop, pathId, courseId } = useContext(AppContext);
  const max = workshop.steps.length;
  const navigate = useNavigate();

  const progressValue = (index * 100) / max;
  const currentStep = workshop.steps[index - 1];

  const user = useSelector((state) => state.Auth.user);

  const handleClose = () => {
    if (courseId) {
      const params = new URLSearchParams();
      if (pathId) params.set("pathId", pathId);
      navigate(`/selecole/course/${courseId}?${params.toString()}`);
    } else {
      setIndex(0);
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 fixed top-0 w-full h-14 z-10 shadow-sm">
      <div className="flex items-center h-full px-4 max-w-full">
        {/* Left: Close/Back + Workshop info */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
          <button
            onClick={handleClose}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
            title={courseId ? "Back to course" : "Close"}>
            {courseId ? <HiOutlineChevronLeft className="w-5 h-5" /> : <MdClose className="w-5 h-5" />}
          </button>

          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
            <img className="w-full h-full object-cover" src={workshop.image} alt="workshop" />
          </div>

          <div className="min-w-0 hidden md:block">
            <div className="text-xs text-gray-500 truncate max-w-[200px]">{workshop.name}</div>
          </div>
        </div>

        {/* Center: Progress with step indicators */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 min-w-0">
          {/* Step title */}
          {currentStep && (
            <div className="text-xs font-medium text-gray-700 truncate max-w-sm mb-1 hidden sm:block">
              {currentStep.title}
            </div>
          )}

          {/* Step progress dots + bar */}
          <div className="flex items-center gap-1 w-full max-w-md">
            {workshop.steps.map((step, i) => {
              const stepNum = i + 1;
              const isCompleted = index > stepNum;
              const isCurrent = index === stepNum;
              return (
                <button
                  key={i}
                  onClick={() => setIndex(stepNum)}
                  title={step.title}
                  className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                    isCompleted
                      ? "bg-blue-500"
                      : isCurrent
                        ? "bg-blue-400 animate-pulse"
                        : "bg-gray-200"
                  }`}
                />
              );
            })}
          </div>

          {/* Step counter */}
          <div className="text-[11px] text-gray-400 mt-1 hidden sm:block">
            {index} / {max}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            title="Share"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={() => {
              copyToClipboard(`https://accounting.selego.co/${workshop.public ? "learn_public" : "learn"}/${workshop._id}?index=${index}`);
              toast.success("URL copied!");
            }}>
            <MdShare className="w-4 h-4" />
          </button>
          {["admin", "super_admin"].includes(user?.role) && (
            <Link
              to={`/selecole/editor/${workshop._id}?step=${index - 1}`}
              title="Edit step"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
              <MdModeEdit className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
